import {
    LoopVaultInfoExpanded,
    MeteoraLoopMetadata,
    StrategyDuration,
    TokenListMetadata,
    UserLoopInfoExpanded,
    ParsedOrcaWhirlpool,
    BaseRouteStepExpanded,
    WhirlpoolOffchain,
    OracleQuote,
    ClmmPositionExpanded,
    LoopWindType
} from "@bridgesplit/abf-sdk";

import { BestQuote, BorrowCap, MarketPrincipalStats, QuoteCollateralInfo } from "./market";
import { AbfLoanExpanded, LoanCollateralExpanded } from "./loan";

export interface LoopExpandedBase {
    collateralToken: TokenListMetadata;
    principalToken: TokenListMetadata;
    vaultIdentifier: string;
    loopVault: LoopVaultInfoExpanded;
    principalOracle: OracleQuote;
    collateralOracle: OracleQuote | null;
    principalStats: MarketPrincipalStats;
    borrowCap: BorrowCap;
    maxCollateralAmount: number | undefined;
    aumLimit?: number;
    loopWindType: LoopWindType;
    inputToken: TokenListMetadata;
}

export interface LoopExpandedMeteora extends LoopExpandedBase {
    meteoraPool: MeteoraLoopMetadata;
    tokenA: TokenListMetadata;
    tokenB: TokenListMetadata;
}

export interface LoopExpandedOrca extends LoopExpandedBase {
    whirlpool: ParsedOrcaWhirlpool;
    whirlpoolOffchain: WhirlpoolOffchain;
}

export type LoopExpanded = LoopExpandedMeteora | LoopExpandedBase | LoopExpandedOrca;

export interface LoopPositionExpanded {
    loanAddress: string;
    userLoopInfo: UserLoopInfoExpanded;
    loanExpanded: AbfLoanExpanded;
    loopExpanded?: LoopExpanded;
    allowedWindTypes: LoopWindType[];
}

// loop can have a default unwind type, if set, its the first value in the array, ie orca is 1, for principal unwind
export function getDefaultWindType(loopPositionExpanded: LoopPositionExpanded | undefined) {
    if (!loopPositionExpanded) return undefined;
    if (loopPositionExpanded.allowedWindTypes.length === 1) {
        return loopPositionExpanded.allowedWindTypes[0];
    }
    return LoopWindType.Collateral;
}

export interface BestLoopQuoteQuery {
    principalMint: string;
    collateralInfo: QuoteCollateralInfo;
    leverageMultiplier: number;
    duration: StrategyDuration;
}

export interface BestLoopQuote {
    bestQuote: BestQuote;
    netApyPct: number;
}

export interface LoopQuoteExpanded extends BestLoopQuote {
    collateralAmount: number;
    leveragedCollateralAmount: number;
    principalAmount: number;
    leverageMultiplier: number;
}

export type ExternalWindQuoteParams = {
    loopExpanded: LoopExpanded | undefined;
    slippagePercentDecimals: number;
    principalAmount: number | undefined;
    inputAmount: number | undefined;
    inputToken?: TokenListMetadata | undefined;
    skip?: boolean;
    routeOverrideParams?: Partial<BaseRouteStepExpanded>;
    clmmPosition?: ClmmPositionExpanded;
    windType: LoopWindType;
};

export type ExternalUnwindQuoteParams = {
    loopPositionExpanded?: LoopPositionExpanded | undefined;
    slippagePercentDecimals: number | undefined;
    skip?: boolean;
    disablePoll?: boolean;
    windType: LoopWindType | undefined;
    rolloverLoopQuoteFetchParams?: any;
    loanCollateral?: LoanCollateralExpanded | undefined;
    loanExpanded?: AbfLoanExpanded | undefined;
    ledgerDebtAmount?: number | undefined;
    debtToRepay?: number | undefined;
    collateralAmount: number | undefined;
};

export interface LoopPositionStats {
    netPositionValue: number;
}

export interface LoopTokenPriceChange {
    start: number;
    end: number;
    change: number;
}
